import { Container, Box } from "@legion-ui/core";
import { useSearchParams } from "react-router-dom";
import { useApiListQuery } from "@/hooks/useApiManagementQuery";
import { ActiveFilters } from "@/components/ActiveFilter";
import { ModalFilter } from "@/components/ModalFilter";
import { TABLE_HEADERS } from "@/constants/apiTable";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton } from "@/components/TableSkeleton";
import { ApiTableRow } from "@/components/ApiTable";
import { PaginationFooter } from "@/components/PaginationFooter";
import { arrayToQuery, queryToArray } from "@/utils/filterQuery";
import { useState } from "react";

export default function ApiManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState(false);

  // ===== READ URL =====
  const page = Number(searchParams.get("page") ?? 1);
  const limit = Number(searchParams.get("limit") ?? 10);
  const search = searchParams.get("search") ?? "";

  const filters = {
    publisher: searchParams.get("publisher") ?? "",
    category: queryToArray(searchParams.get("category")),
    typePlan: queryToArray(searchParams.get("typePlan")),
    status: queryToArray(searchParams.get("status")),
  };

  // ===== FETCH =====
  const { data, isLoading } = useApiListQuery({
    search,
    page,
    limit,
    publisherUsername: filters.publisher,
    category: arrayToQuery(filters.category),
    typePlan: arrayToQuery(filters.typePlan),
    status: arrayToQuery(filters.status),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const updateParams = (cb: (p: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      cb(p);
      return p;
    });
  };

  return (
    <Container>
      <Box mb="1rem">
        <h1 className="text-2xl font-bold">API Management</h1>
        <p className="text-gray-600 mt-1">Monitor API and Subscription</p>

        <div className="flex gap-2 mt-4">
          <input
            value={search}
            onChange={(e) =>
              updateParams((p) => {
                p.set("search", e.target.value);
                p.set("page", "1");
              })
            }
            placeholder="Search API Name"
            className="flex-1 px-3 py-2 border rounded-md"
          />

          <button
            onClick={() => setOpenFilter(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Filter
          </button>
        </div>
      </Box>

      <ActiveFilters filters={filters} />

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              {TABLE_HEADERS.map((h) => (
                <th key={h} className="p-3">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          {isLoading ? (
            <TableSkeleton columns={TABLE_HEADERS.length} />
          ) : items.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={TABLE_HEADERS.length}>
                  <EmptyState />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {items.map((item, i) => (
                <ApiTableRow
                  key={item.id}
                  index={(page - 1) * limit + i + 1}
                  item={item}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {!isLoading && total > 0 && (
        <PaginationFooter
          page={page}
          total={total}
          onChangePage={(p) =>
            updateParams((sp) => sp.set("page", String(p)))
          }
          onChangeLimit={(l) =>
            updateParams((sp) => {
              sp.set("limit", String(l));
              sp.set("page", "1");
            })
          }
        />
      )}

      <ModalFilter
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        initialValues={filters}
        onApply={(f) =>
          updateParams((p) => {
            p.set("page", "1");

            f.publisher ? p.set("publisher", f.publisher) : p.delete("publisher");
            f.category.length
              ? p.set("category", arrayToQuery(f.category))
              : p.delete("category");
            f.typePlan.length
              ? p.set("typePlan", arrayToQuery(f.typePlan))
              : p.delete("typePlan");
            f.status.length
              ? p.set("status", arrayToQuery(f.status))
              : p.delete("status");
          })
        }
      />
    </Container>
  );
}
